import apiClient from './apiService';

// ============================================================================
// UMA (User Manual Agent) typed service (Phase 117, plan 06 / UMA-SHOT +
// UMA-GUIDE). Mirrors backend/models/uma_schemas.py field-for-field. Every
// call goes through the shared `apiClient` singleton so the Bearer token is
// injected by its request interceptor — never build a base URL/token by hand,
// never a raw browser `fetch` call.
// ============================================================================

export interface AskRequest {
  question: string;
  session_id?: string;
}

export interface ManualStep {
  number: number;
  instruction: string;
  screenshot?: string | null;
}

export interface Citation {
  chunk_id: string;
  chapter: string;
  section: string;
}

export type ManualAnswerType = 'steps' | 'partial' | 'clarification' | 'refusal';

export interface ManualAnswer {
  answer_type: ManualAnswerType;
  summary: string;
  steps: ManualStep[];
  citations: Citation[];
  screenshots: string[];
  gaps?: string | null;
}

export interface GuidelinesResponse {
  guidelines: string[];
}

export type FeedbackRating = 'good' | 'bad';

export interface FeedbackRequest {
  rating: FeedbackRating;
  question: string;
  answer: ManualAnswer;
  session_id?: string;
  note?: string;
}

export interface FeedbackResponse {
  stored: boolean;
  id?: number | null;
}

// The shared apiClient enforces a 10s timeout suited to ordinary CRUD calls,
// but a grounded /ask round-trips a full LLM generation (embed → retrieve →
// Claude, plus a possible one-shot corrective retry) that routinely runs
// 15–40s. Give this ONE call a generous per-request timeout so answers (and
// their screenshots) aren't cut off with "timeout of 10000ms exceeded".
const UMA_ASK_TIMEOUT_MS = 90000;

/**
 * POST /api/v1/uma/ask — grounded Q&A over the user manual.
 *
 * May reject with a 503 response until the manual index + LLM keys are
 * provisioned (plan 117-07) — this function does not swallow that; callers
 * (UmaChatLauncher) must catch it and degrade gracefully.
 */
export const askManual = async (payload: AskRequest): Promise<ManualAnswer> => {
  const response = await apiClient.post<ManualAnswer>('/api/v1/uma/ask', payload, {
    timeout: UMA_ASK_TIMEOUT_MS,
  });
  return response.data;
};

/**
 * GET /api/v1/uma/guidelines — the exact 10 guidelines the agent enforces
 * (UMA-GUIDE). Displayed guidelines are sourced from here so they can never
 * drift from the enforced constant on the backend.
 */
export const fetchGuidelines = async (): Promise<string[]> => {
  const response = await apiClient.get<GuidelinesResponse>('/api/v1/uma/guidelines');
  return response.data.guidelines;
};

/**
 * Fetch a JWT-gated manual screenshot (`/api/v1/uma/assets/{filename}`)
 * through the authed client and hand back a blob object URL. A plain
 * `<img src>` would 401 — this is the ONLY authed-image pattern for the
 * manual agent (mirrors fileService.open in auditService.ts). The
 * `screenshots[]` / `step.screenshot` entries returned by askManual are
 * already full `/api/v1/uma/assets/...` URLs (resolved server-side by the
 * router), so `url` here is passed straight through.
 *
 * Caller owns revocation — see useObjectUrl.ts's useManualAsset, which
 * revokes on unmount/url-change (a chat session renders many screenshots
 * over its lifetime, so per-image cleanup replaces a one-shot timer).
 */
export const fetchManualAsset = async (url: string): Promise<string> => {
  const res = await apiClient.get(url, { responseType: 'blob' });
  const type = (res.headers?.['content-type'] as string) || 'image/png';
  return URL.createObjectURL(new Blob([res.data], { type }));
};

/**
 * POST /api/v1/uma/feedback — record a good/bad rating on an answer.
 *
 * A 'bad' rating stores the question + the answer the system gave, for the
 * team's retrospective self-learning review. Best-effort telemetry: the
 * backend never 500s here (returns { stored: false } on a storage hiccup),
 * so callers can fire-and-forget and simply reflect the returned `stored`.
 */
export const submitFeedback = async (
  payload: FeedbackRequest
): Promise<FeedbackResponse> => {
  const response = await apiClient.post<FeedbackResponse>(
    '/api/v1/uma/feedback',
    payload
  );
  return response.data;
};
